import {useTranslation} from 'react-i18next';
import {useMemo, useState, useCallback} from 'react';
import useScreenSize from '../../../../../hooks/useScreenSize';
import AppButton from '../../../../../components/common/AppButton';
import AppTextInput from '../../../../../components/common/AppTextInput';
import AppConfirmationDialog from '../../../../../components/common/AppConfirmationDialog';
import {SVG} from '../../../../../constants/assetLists';
import {useFormsContext} from '../../../../../context/forms/FormsContextProvider';
import {useSnackbar} from '../../../../../context/SnackbarContextProvider';
import FormsMenuSection from './FormsMenuSection';
import {FormStatuses} from '../../../../../services/operations/forms';
import {useFormsMutations} from '../../../../../hooks/useFormsHook';

// Note: These imports will be used when APIs are ready
// import {useMutation, useQueryClient} from '@tanstack/react-query';
// import QUERY_KEYS from '../../../../../constants/queryKeys';
// import {handleBulkArchiveFormService} from '../../../../../services/operations/forms';

function Header() {
  /** ---------------- Language & Layout ---------------- */
  const {t, i18n} = useTranslation();
  const isHebrew = useMemo(() => i18n.language === 'heb', [i18n.language]);

  const screenSize = useScreenSize();
  const isLarge = screenSize === 'xl' || screenSize === '2xl';

  /** ---------------- External Hooks ---------------- */
  const {showSnackbar} = useSnackbar();
  // const queryClient = useQueryClient(); // Uncomment when APIs are ready

  const {
    allForms,
    setSelectedForm,
    selectedForms,
    setSelectedForms,
    searchText,
    setSearchText,
    setShowAddEditDialog,
    setCopyMode,
    daySummaryFormFilter,
    clearDaySummaryFormFilter,
  } = useFormsContext();
  const {changeFormStatusMutation} = useFormsMutations();

  /** ---------------- Local State ---------------- */
  const [showArchiveConfirmation, setShowArchiveConfirmation] = useState(false);

  /** ---------------- Derived Values ---------------- */
  const isOneSelected = selectedForms.length === 1;
  const disableArchive = !(selectedForms.length >= 1);

  const totalForms = allForms?.total ?? 0;

  // Check if any selected form has 'archived' status
  const hasSelectedArchivedForm = useMemo(() => {
    if (!selectedForms.length || !allForms?.forms) {
      return false;
    }

    return selectedForms.some(formId => {
      const form = allForms.forms.find((f: any) => f._id === formId);
      return form?.status === FormStatuses.ARCHIVED;
    });
  }, [selectedForms, allForms?.forms]);

  /** ---------------- Mutations ---------------- */
  // Note: Uncomment this when APIs are ready
  // const {mutate: handleBulkArchiveForm} = useMutation({
  //   mutationFn: (payload: {
  //     formIds: string[];
  //     status: 'archived';
  //   }) => handleBulkArchiveFormService(payload),
  //
  //   onSuccess: data => {
  //     queryClient.invalidateQueries({
  //       queryKey: [QUERY_KEYS.GET_ALL_FORMS],
  //     });
  //
  //     showSnackbar({
  //       message: data?.message || 'הטופס הועבר לארכיון בהצלחה',
  //       type: 'success',
  //     });
  //   },
  // });

  /** ---------------- Handlers ---------------- */
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);
    },
    [setSearchText],
  );

  const clearSearch = useCallback(() => {
    setSearchText('');
  }, [setSearchText]);

  const handleClearDaySummaryFilter = useCallback(() => {
    clearDaySummaryFormFilter();
    setSearchText('');
  }, [clearDaySummaryFormFilter, setSearchText]);

  const handleArchiveConfirm = useCallback(() => {
    if (changeFormStatusMutation.isPending) return;
    changeFormStatusMutation.mutate({
      formIds: selectedForms as string[],
      newStatus: FormStatuses.ARCHIVED,
    });

    // For now, just show a success message (mock behavior)
    showSnackbar({
      message: isHebrew
        ? 'הטופס הועבר לארכיון בהצלחה'
        : 'Form archived successfully',
      type: 'success',
    });

    setSelectedForms([]);
    setShowArchiveConfirmation(false);
  }, [
    changeFormStatusMutation,
    selectedForms,
    setSelectedForms,
    showSnackbar,
    isHebrew,
  ]);

  const openAddEditForm = useCallback((formId = selectedForms[0]) => {
    setSelectedForm(formId);
    setSelectedForms([]);

    setShowAddEditDialog(true);
  }, [selectedForms, setSelectedForm, setSelectedForms, setShowAddEditDialog]);

  const openCopyForm = () => {
    setSelectedForm(selectedForms[0]);
    setSelectedForms([]);
    setCopyMode(true);
    setShowAddEditDialog(true);
  };

  const handleAddForm = () => {
    setSelectedForm(null);
    setSelectedForms([]);
    setShowAddEditDialog(true);
  };

  /** ---------------- UI ---------------- */
  return (
    <>
      <div
        className={`w-full py-4 bg-white px-4 flex flex-wrap items-center justify-between gap-3 shadow-md z-2
          ${isLarge ? (isHebrew ? 'rounded-tl-[32px]' : 'rounded-tr-[32px]') : ''}
        `}>
        <div className="flex-1 flex flex-wrap items-center gap-3">
          {/* Left actions */}
          <div className="flex items-center gap-2">
            <AppButton
              variant="primary"
              py="12px"
              iconStart={
                <img src={SVG.addWhiteIcon} className="w-[12px] h-[12px]" />
              }
              onClick={handleAddForm}>
              {t('forms.actions.add')}
            </AppButton>

            <AppButton
              variant="primary"
              py="12px"
              disabled={!isOneSelected}
              iconStart={
                <img
                  src={SVG.editPencilWhiteIcon}
                  className="w-[14px] h-[14px]"
                />
              }
              onClick={() => openAddEditForm()}>
              {t('forms.actions.edit')}
            </AppButton>

            <AppButton
              variant="primary"
              py="12px"
              disabled={!isOneSelected}
              iconStart={
                <img src={SVG.copyWhiteIcon} className="w-[18px] h-[18px]" />
              }
              onClick={() => openCopyForm()}>
              {t('forms.actions.copy')}
            </AppButton>

            <AppButton
              variant="primary"
              py="12px"
              disabled={disableArchive || hasSelectedArchivedForm}
              iconStart={
                <img src={SVG.whiteCloseIcon} className="w-[14px] h-[14px]" />
              }
              onClick={() => setShowArchiveConfirmation(true)}>
              {t('forms.actions.archive')}
            </AppButton>
          </div>

          {/* Search */}
          <div className="flex items-center gap-3">
            <AppTextInput
              placeholder={t('forms.search.placeholder')}
              value={searchText}
              onChange={handleSearchChange}
              containerClassName="!w-[499px]"
              className="bg-transparent !rounded-[28px] pl-16"
              endIcon={
                <div className="flex items-center gap-1">
                  {searchText && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="w-[24px] h-[24px] flex justify-center items-center rounded-full hover:bg-gray20">
                      <img
                        src={SVG.closeDarkIcon}
                        className="w-[12px] h-[12px]"
                        alt="clear"
                      />
                    </button>
                  )}
                  <div
                    className={`w-[24px] h-[24px] flex justify-center items-center rounded-full ${
                      searchText ? 'bg-aqua' : ''
                    }`}>
                    <img src={SVG.searchIcon} className="w-[18px] h-[18px]" />
                  </div>
                </div>
              }
            />

            <FormsMenuSection />

            {daySummaryFormFilter && (
              <button
                type="button"
                onClick={handleClearDaySummaryFilter}
                className="flex items-center gap-2 rounded-lg px-3 py-2 bg-pastelYellow ">
                <span className="text-sm text-[#1D192B]">
                  {daySummaryFormFilter.clearLabel ?? 'ביטול סינון סיכום'}
                </span>
                <img
                  src={SVG.closeDarkIcon}
                  alt="clear"
                  className="w-[11px] h-[11px] object-contain"
                />
              </button>
            )}
          </div>
        </div>

        <div>
          <p className="m-0 text-black">
            {totalForms} {t('forms.count')}
          </p>
        </div>
      </div>

      <AppConfirmationDialog
        open={showArchiveConfirmation}
        onClose={() => setShowArchiveConfirmation(false)}
        onConfirm={handleArchiveConfirm}
        icon={<img src={SVG.deleteRedIcon} className="w-[48px] h-[48px]" />}
        title={t('forms.archiveConfirm.title')}
        subTitle={t('forms.archiveConfirm.message')}
        confirmBtnText={t('common.yes')}
        cancelBtnText={t('common.no')}
      />
    </>
  );
}

export default Header;
